import React from 'react';
import { Pill } from '../index';

/**
 * InheritanceChain — visual ORG → TENANT → AGENT cascade for trust scoring config.
 *
 * Each scope shows whether it has a CUSTOM override or INHERITS from the
 * parent. The "effective" config is whichever scope is the most specific
 * with a custom override (AGENT > TENANT > ORG > platform default).
 *
 * Phase A10 / G310 — used in ScoringConfigTab to give operators a clean
 * mental model of "where the config they're editing actually comes from"
 * before they save.
 *
 * @param {Object} props
 * @param {'ORG'|'TENANT'|'AGENT'} props.activeScope
 * @param {Object<string, boolean>} props.customAt   — { ORG: bool, TENANT: bool, AGENT: bool }
 * @param {Function} [props.onScopeChange]
 */
export function InheritanceChain({ activeScope, customAt = {}, onScopeChange }) {
  const SCOPES = ['ORG', 'TENANT', 'AGENT'];

  return (
    <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-4">
      <div className="text-[10.5px] font-semibold text-v2-ink-muted uppercase tracking-wider mb-3">
        Inheritance chain
      </div>

      <div className="flex items-center gap-2">
        {SCOPES.map((scope, idx) => {
          const isActive = scope === activeScope;
          const isCustom = !!customAt[scope];
          return (
            <React.Fragment key={scope}>
              <button
                type="button"
                onClick={() => onScopeChange?.(scope)}
                aria-pressed={isActive}
                className={`flex-1 px-3 py-2.5 rounded-v2-sm transition-all border ${
                  isActive
                    ? 'bg-v2-action-soft border-v2-action shadow-v2-sm'
                    : 'bg-v2-surface border-v2-border-subtle hover:border-v2-action/50'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className={`text-[11.5px] font-semibold ${isActive ? 'text-v2-action-strong' : 'text-v2-ink'}`}>
                    {scope}
                  </span>
                  <Pill
                    tone={isCustom ? 'success' : 'warning'}
                    size="sm"
                    dot
                  >
                    {isCustom ? 'Custom' : 'Inherited'}
                  </Pill>
                </div>
                <div className="text-[10.5px] text-v2-ink-muted text-left">
                  {scope === 'ORG'    && 'Organisation-wide default'}
                  {scope === 'TENANT' && 'Tenant override of org'}
                  {scope === 'AGENT'  && 'Per-agent override'}
                </div>
              </button>
              {idx < SCOPES.length - 1 && (
                <Arrow active={customAt[scope] && SCOPES.slice(idx + 1).some((s) => customAt[s])} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <p className="text-[10.5px] text-v2-ink-muted mt-3 m-0">
        The most specific custom override wins. Agents fall back to tenant, tenants to org,
        and org to the platform default if no override is present.
      </p>
    </div>
  );
}

function Arrow({ active }) {
  return (
    <svg width="16" height="10" viewBox="0 0 16 10" className="shrink-0" aria-hidden="true">
      <path
        d="M0 5 L12 5 M8 1 L12 5 L8 9"
        fill="none"
        stroke={active ? 'var(--v2-action)' : 'var(--v2-border)'}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default InheritanceChain;
